import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import {
  AppCheckbox,
  AppCheckboxFormLabel,
  AppForm,
  AppFormLabel,
  AppTextField,
  AppTypography,
} from '@/components/atoms';
import { A2_DISEASE_OTHER_ID } from '@/constants/consentForm';
import type { ConsentFormData } from '@/schemas/consentSchema';
import type { LabelChecks, SetLabelChecks } from '../types';
import { ensureArray } from '../utils';

interface DiseaseNameFieldsProps {
  control: Control<ConsentFormData>;
  diseaseName: unknown[];
  diseaseNameOptions: Array<{ label: string; value: string }>;
  isA2: boolean;
  labelChecks: LabelChecks;
  markDiseaseUserAction: () => void;
  markInjuryNameEdited: () => void;
  setLabelChecks: SetLabelChecks;
  setValue: UseFormSetValue<ConsentFormData>;
}

export const DiseaseNameFields: React.FC<DiseaseNameFieldsProps> = ({
  control,
  diseaseName,
  diseaseNameOptions,
  isA2,
  labelChecks,
  markDiseaseUserAction,
  markInjuryNameEdited,
  setLabelChecks,
  setValue,
}) => {
  if (isA2) {
    return (
      <>
        {/* 傷病名① (disease_name checkboxes) — A2 only, placed first */}
        <div className="flex items-center gap-2">
          <AppCheckboxFormLabel
            label="傷病名①"
            required
            tabIndex={-1}
            checked={!!labelChecks.disease_name}
            onChange={e => setLabelChecks(prev => ({ ...prev, disease_name: e.target.checked }))}
            className="flex-1"
          />
        </div>
        <div>
          <Controller
            name="disease_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                <AppCheckbox.Group
                  {...field}
                  value={ensureArray(field.value)}
                  className="grid w-full grid-cols-[repeat(auto-fit,minmax(112px,1fr))] gap-1 [&_.ant-checkbox+span]:whitespace-nowrap"
                  onChange={nextValues => {
                    markDiseaseUserAction();
                    const prevValues = ensureArray(field.value);
                    const nextArr = ensureArray(nextValues);
                    const hasOtherBefore = prevValues.includes(A2_DISEASE_OTHER_ID);
                    const hasOtherAfter = nextArr.includes(A2_DISEASE_OTHER_ID);

                    field.onChange(nextArr);

                    if (hasOtherBefore && !hasOtherAfter) {
                      setValue('disease_name_other', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                  }}
                  options={diseaseNameOptions}
                />
              </AppForm.Item>
            )}
          />
          <div className="mt-2">
            <Controller
              name="disease_name_other"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                  <AppTextField
                    {...field}
                    value={field.value || ''}
                    maxLength={255}
                    disabled={!diseaseName?.includes(A2_DISEASE_OTHER_ID)}
                    status={error ? 'error' : ''}
                    onChange={e => {
                      markDiseaseUserAction();
                      field.onChange(e);
                    }}
                  />
                </AppForm.Item>
              )}
            />
          </div>
          <AppTypography.Text type="secondary" className="block mt-1 text-xs">
            ※ 療養費支給申請書の「傷病名及び症状」と、同意書の「傷病名」に印字します
          </AppTypography.Text>
        </div>

        {/* 傷病名② (injury_name text) — A2 only, placed second */}
        <div className="flex items-center gap-2">
          <AppFormLabel label="傷病名②" required className="ml-[24.4px] flex-1" />
        </div>
        <InjuryNameField
          control={control}
          markDiseaseUserAction={markDiseaseUserAction}
          markInjuryNameEdited={markInjuryNameEdited}
          wrapInput
        />
      </>
    );
  }

  return (
    <>
      {/* 傷病名① = disease_name_r2 — R2 only, placed first */}
      <div className="flex items-center gap-2">
        <AppCheckboxFormLabel
          label="傷病名①"
          required
          checked={!!labelChecks.disease_name}
          onChange={e => setLabelChecks(prev => ({ ...prev, disease_name: e.target.checked }))}
          className="flex-1"
        />
      </div>
      <div>
        <Controller
          name="disease_name_r2"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
              <AppTextField
                {...field}
                value={field.value || ''}
                maxLength={255}
                status={error ? 'error' : ''}
                className="w-full"
                onChange={e => {
                  markDiseaseUserAction();
                  field.onChange(e);
                }}
              />
            </AppForm.Item>
          )}
        />
        <AppTypography.Text type="secondary" className="block mt-1 text-xs">
          ※ 療養費支給申請書の「傷病名及び症状」と、同意書の「傷病名」に印字します
        </AppTypography.Text>
      </div>

      {/* 傷病名② = injury_name — R2 only, placed second */}
      <div className="flex items-center gap-2">
        <AppFormLabel label="傷病名②" required className="ml-[24.4px] flex-1" />
      </div>
      <InjuryNameField
        control={control}
        markDiseaseUserAction={markDiseaseUserAction}
        markInjuryNameEdited={markInjuryNameEdited}
      />
    </>
  );
};

interface InjuryNameFieldProps {
  control: Control<ConsentFormData>;
  markDiseaseUserAction: () => void;
  markInjuryNameEdited: () => void;
  wrapInput?: boolean;
}

const InjuryNameField: React.FC<InjuryNameFieldProps> = ({
  control,
  markDiseaseUserAction,
  markInjuryNameEdited,
  wrapInput = false,
}) => {
  const input = (
    <Controller
      name="injury_name"
      control={control}
      render={({ field, fieldState: { error } }) => (
        <AppForm.Item
          validateStatus={error ? 'error' : undefined}
          help={error?.message}
          className={wrapInput ? 'flex-1' : undefined}
        >
          <AppTextField
            {...field}
            value={field.value || ''}
            onChange={e => {
              markInjuryNameEdited();
              field.onChange(e);
            }}
            maxLength={255}
            status={error ? 'error' : ''}
            className="w-full"
          />
        </AppForm.Item>
      )}
    />
  );

  return (
    <div>
      <div className="mb-1">
        <Controller
          name="same_content"
          control={control}
          render={({ field }) => (
            <AppCheckbox
              {...field}
              checked={field.value}
              onChange={e => {
                markDiseaseUserAction();
                field.onChange(e.target.checked);
              }}
            >
              <span className="text-red-500">傷病名①と同内容</span>
            </AppCheckbox>
          )}
        />
      </div>
      {wrapInput ? <div className="flex gap-2">{input}</div> : input}
      <AppTypography.Text type="secondary" className="block mt-1 text-xs">
        ※ 療養費支給申請書の「傷病名、発症又は負傷の原因及びその経過」に印字します
      </AppTypography.Text>
    </div>
  );
};
